import { Outlet, Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AdminSidebar } from "./admin-sidebar";
import { AdminTopBar } from "./admin-topbar";
import { CollapsibleSidebar } from "./collapsible-sidebar";
import { MobileAdminShell } from "./mobile-admin-shell";
import { companiesApi } from "@/api/companies";
import { queryKeys } from "@/lib/query-keys";
import { useAuth } from "@/context/auth-context";
import { useIsMobile } from "@/hooks/use-is-mobile";

function AdminLayout() {
  return (
    <div className="flex h-screen w-screen overflow-hidden bg-background">
      <CollapsibleSidebar>
        <AdminSidebar />
      </CollapsibleSidebar>
      <main className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <AdminTopBar />
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

export function AdminShell() {
  const { isInstanceAdmin } = useAuth();
  const isMobile = useIsMobile();

  // Non-admin users: resolve their companies to redirect to their tenant
  const { data: companies, isLoading } = useQuery({
    queryKey: queryKeys.companies.all,
    queryFn: companiesApi.list,
    enabled: !isInstanceAdmin,
  });

  // Instance admins go straight to the admin dashboard
  if (isInstanceAdmin) {
    return isMobile ? <MobileAdminShell /> : <AdminLayout />;
  }

  if (isLoading) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-background">
        <p className="text-sm text-text-muted animate-pulse">Loading...</p>
      </div>
    );
  }

  // Tenant-only user with exactly one company: redirect to their tenant
  if (companies && companies.length === 1) {
    return <Navigate to={`/tenants/${companies[0].id}/dashboard`} replace />;
  }

  // Tenant user with multiple companies — show admin shell so they can pick
  if (companies && companies.length > 1) {
    return isMobile ? <MobileAdminShell /> : <AdminLayout />;
  }

  // No companies at all — user has no access anywhere
  return (
    <div className="flex h-screen w-screen items-center justify-center bg-background px-4">
      <div className="text-center">
        <p className="text-sm text-text-primary font-medium">No Access</p>
        <p className="text-xs text-text-muted mt-1">
          You don't have access to any tenants. Contact your administrator.
        </p>
      </div>
    </div>
  );
}
